import { Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

interface Props {
  detailedSectorId?: string | undefined;
}

interface Profile {
  esName: string;
  enName: string;
  margin: number;
  tipsEs: [string, string, string];
  tipsEn: [string, string, string];
}

const PROFILES: Record<string, Profile> = {
  fashion: {
    esName: "Tienda de Ropa / Zapatería",
    enName: "Clothing / Shoe Store",
    margin: 55,
    tipsEs: [
      "La rotación de stock por temporadas es vital; el producto inmovilizado hunde tu rentabilidad.",
      "El escaparate y el 'visual merchandising' son tus mejores comerciales a pie de calle.",
      "Calcula siempre una merma de beneficio para la época obligatoria de rebajas.",
    ],
    tipsEn: [
      "Seasonal stock rotation is vital; idle inventory destroys profitability.",
      "Window display and visual merchandising are your best street-level sales reps.",
      "Always factor in a margin loss for mandatory sale periods.",
    ],
  },
  hospitality: {
    esName: "Restaurante / Bar / Cafetería",
    enName: "Restaurant / Bar / Café",
    margin: 65,
    tipsEs: [
      "El éxito financiero depende de los escandallos (medir al gramo el coste de cada plato/bebida).",
      "La rotación de mesas en horas pico define tu techo máximo de facturación diaria.",
      "Controlar los turnos del personal para evitar horas muertas es tu mayor reto de gestión.",
    ],
    tipsEn: [
      "Financial success depends on recipe costing (measuring every gram of each dish/drink).",
      "Table rotation during peak hours defines your maximum daily revenue ceiling.",
      "Controlling staff shifts to avoid dead hours is your biggest management challenge.",
    ],
  },
  food: {
    esName: "Panadería / Supermercado / Alimentación",
    enName: "Bakery / Supermarket / Grocery",
    margin: 30,
    tipsEs: [
      "Trabajas con márgenes muy estrechos, por lo que dependes de un volumen de tráfico peatonal masivo y constante.",
      "La frescura diaria del producto es la clave absoluta de la retención del cliente.",
      "Optimiza la compra por impulso colocando productos de alto margen cerca de la caja.",
    ],
    tipsEn: [
      "You work with very tight margins, so you depend on massive and constant foot traffic.",
      "Daily product freshness is the absolute key to customer retention.",
      "Optimize impulse buys by placing high-margin products near the checkout.",
    ],
  },
  beauty: {
    esName: "Peluquería / Centro de Estética",
    enName: "Hair Salon / Beauty Center",
    margin: 80,
    tipsEs: [
      "El local es el gancho, pero el beneficio real escala con la venta cruzada de productos cosméticos (retail).",
      "Optimizar la agenda para no tener 'huecos vacíos' entre clientes es tu prioridad diaria.",
      "La fidelización es extrema; un cliente contento asegura ingresos recurrentes mensuales.",
    ],
    tipsEn: [
      "The location is the hook, but real profit scales with cross-selling cosmetic retail products.",
      "Optimizing the agenda to avoid empty slots between clients is your daily priority.",
      "Loyalty is extreme; a happy customer secures recurring monthly revenue.",
    ],
  },
  pharma: {
    esName: "Farmacia / Parafarmacia",
    enName: "Pharmacy / Parapharmacy",
    margin: 45,
    tipsEs: [
      "La venta por consejo farmacéutico/estético es lo que te diferencia de la venta online.",
      "Control exhaustivo del inventario, stock inmovilizado y fechas de caducidad.",
      "Alta fidelidad de barrio; la ubicación estratégica de tu cruz luminosa marca la diferencia.",
    ],
    tipsEn: [
      "Advisory selling is what differentiates you from online sales.",
      "Exhaustive control of inventory, idle stock and expiration dates.",
      "Strong neighborhood loyalty; the strategic location of your sign makes the difference.",
    ],
  },
  highTicket: {
    esName: "Muebles y Decoración / Joyería / Ticket Alto",
    enName: "Furniture / Jewelry / High-Ticket",
    margin: 50,
    tipsEs: [
      "Tienes muy pocos tickets diarios, por lo que la tasa de conversión por cada persona que entra debe ser altísima.",
      "La atención al cliente experta y personalizada es el núcleo de tu negocio.",
      "Los costes de financiación y exposición del inventario requieren un flujo de caja muy saneado.",
    ],
    tipsEn: [
      "You have very few daily tickets, so conversion rate per visitor must be very high.",
      "Expert, personalized customer service is the core of your business.",
      "Financing and inventory display costs require very healthy cash flow.",
    ],
  },
};

const MAP: Record<string, keyof typeof PROFILES> = {
  tienda_ropa: "fashion",
  zapateria: "fashion",
  joyeria: "highTicket",
  muebles: "highTicket",
  restaurante: "hospitality",
  bar: "hospitality",
  cafeteria: "hospitality",
  panaderia: "food",
  supermercado: "food",
  alimentacion: "food",
  peluqueria: "beauty",
  estetica: "beauty",
  farmacia: "pharma",
  parafarmacia: "pharma",
  otro: "fashion",
};

const SectorAuthorityCard = ({ detailedSectorId }: Props) => {
  const { lang } = useLanguage();
  const key = MAP[detailedSectorId ?? "otro"] ?? "fashion";
  const p = PROFILES[key]!;
  const es = lang === "es";
  const tips = es ? p.tipsEs : p.tipsEn;
  const name = es ? p.esName : p.enName;

  return (
    <div className="rounded-xl border border-yellow-300/70 bg-yellow-50/40 p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-yellow-700" strokeWidth={1.75} />
        <h3 className="text-sm font-bold text-foreground">
          {es ? "Conocemos tu sector: Claves para" : "We know your sector: Keys for"}{" "}
          <span className="text-yellow-900">{name}</span>
        </h3>
      </div>
      <ul className="space-y-1.5 mb-3">
        {tips.map((tip, i) => (
          <li key={i} className="flex gap-2 text-xs text-foreground/80 leading-relaxed">
            <span className="text-yellow-700 font-bold shrink-0">·</span>
            <span>{tip}</span>
          </li>
        ))}
      </ul>
      <div className="pt-2 border-t border-yellow-300/50">
        <p className="text-[11px] font-semibold text-yellow-900">
          {es ? "Margen bruto de referencia en tu sector:" : "Reference gross margin in your sector:"}{" "}
          <span className="text-sm">{p.margin}%</span>
        </p>
      </div>
    </div>
  );
};

export default SectorAuthorityCard;
